import React from 'react';

import * as ResourceApi from 'api/resources';
import IndexedSubSectionWrapper from "hoc/IndexedSubSectionWrapper";
import {createButtonSectionFactory,createButtonSectionFactoryWithAdditionalArgs} from "hoc/CreateButtonSection";
import {FBCommentQuestionToolTip} from 'components/break/tooltips/breaks-steps-section-tool-tips';
import {hasAllUserTagPermissions} from 'utils/DBUtils';

import {FormattedMessage, IntlProvider} from 'react-intl';
import {enUSmessage} from 'i18n/en-US.js';

import {
    TextTriviaHeadlineToolTip,
    TextTriviaIntroCopyToolTip,
    TextTriviaDefaultButtonBkgdColorToolTip,
    TextTriviaDefaultButtonTextColorToolTip,
    TextTriviaQuestionToolTip,
    TextTriviaQuestionImageToolTip,
    TextTriviaAnswerToolTip,
    TextTriviaAnswerImageToolTip,
    TextTriviaAnswerCorrectToolTip,
    TextTriviaAnswerEditButtonColorsToolTip,
    TextTriviaAnswerButtonBkgdColorToolTip,
    TextTriviaAnswerButtonTextColorToolTip,
    TextTriviaQuestionResponseTextToolTip,
    TextTriviaQuestionResponseImageToolTip,
    TextTriviaResultTitleToolTip,
    TextTriviaResultDescriptionToolTip,
    TextTriviaResultMinCorrectToolTip,
    TextTriviaResultMaxCorrectToolTip,
    TextTriviaResultImageToolTip,
    TextTriviaUseDifferentSocialImageToolTip,
    TextTriviaResultSocialImageToolTip
} from 'components/break/tooltips/text-trivia-tool-tips';

export default {
    name: "textTrivia",
    subSection: true,
    resource: true,
    hideIfResourceIsNoU: true,

    fields: [
        {
            conditionalSection: true,
            name: "useTimer",
            required: false,
            props: {
                labelKey: 'editbreak.steps.text_trivia.use_timer',
            },
            fields: [
                {
                    type: 'number', // seconds
                    name: 'questionDuration',
                    required: true,
                    props: {
                        labelKey: 'editbreak.steps.text_trivia.question_duration',
                        placeholderKey: 'editbreak.steps.text_trivia.question_duration.placeholder',
                    }
                }
            ]
        },
        {
            type: 'contenteditable',
            name: 'headline',
            required: true,
            // Headline is only required if there is more than one step
            showField: (proxy) => (proxy.currentBaseResource.steps && proxy.currentBaseResource.steps.length > 1),

            props: {
                labelKey: 'editbreak.steps.text_trivia.headline',
                helpContent: <TextTriviaHeadlineToolTip/>,
                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.headline'}/></IntlProvider>
            }
        },
        {
            type: 'contenteditable',
            name: 'introCopy',
            required: false,
            props: {
              labelKey: 'editbreak.steps.text_trivia.intro_copy',
              placeholderKey: 'editbreak.steps.text_trivia.intro_copy.placeholder',
              helpContent: <TextTriviaIntroCopyToolTip/>,
              helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.intro_copy'}/></IntlProvider>,
              maxLength: 500,
              validationRules: [
                  {
                      type: 'MarkupTextLimit',
                      params: {
                          limit: 500
                      }
                  }
              ]
            }
        },
        {
            type: 'text',
            name: 'commentQuestion',
            showField: (proxy) => (proxy.currentBaseResource.showFBComments && proxy.currentBaseResource.sponsoredType !== 'sponsored' ),
            props: {
                labelKey: 'editbreak.steps.related_breaks.commentQuestion',
                helpContent: <FBCommentQuestionToolTip/>,
                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.breaks_steps.fbCommentQuestion'}/></IntlProvider>
            }
        },
        {
            type: 'color',
            name: 'defaultAnswerBgColor',
            required: true,
            showField: (proxy) =>  (proxy.currentBaseResource.sponsoredType === 'sponsored'),

            props: {
                labelKey: 'editbreak.steps.text_trivia.defaultAnswerBgColor',
                helpContent: <TextTriviaDefaultButtonBkgdColorToolTip/>,
                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.defaultAnswerBgColor'}/></IntlProvider>
            }
        },
        {
            type: 'color',
            name: 'defaultAnswerTextColor',
            showField: (proxy) =>  (proxy.currentBaseResource.sponsoredType === 'sponsored'),
            required: true,

            props: {
                labelKey: 'editbreak.steps.text_trivia.defaultAnswerTextColor',
                helpContent: <TextTriviaDefaultButtonTextColorToolTip/>,
                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.defaultAnswerTextColor'}/></IntlProvider>
            }
        },
        {
            name: 'questions',
            subSection: true,
            resource: true,
            collection: true,
            sectionWrapper: IndexedSubSectionWrapper,
            deletable: true,
            canDelete: (formProxy) => {
              var trivia = formProxy.parentCurrentResource;
              return trivia.questions && trivia.questions.length > 1;
            },
            sortable: true,
            sortableName: "questionsOrder",
            toggleSortLabelKey: 'editbreak.steps.text_trivia.question.reorder_toggle',
            resourceType: ResourceApi.Types.TextTriviaQuestion,

            props: {
                headerKey: 'editbreak.steps.text_trivia.question.header',
                getCustomSectionHeader: (question, sectionIndex) => {
                  return  question.type === 'TEXT'
                          && question.question
                          && question.question.trim() !== "" ? "Question " + (sectionIndex+1) + ": " + question.question : null;
                },
                headerIcon: 'question'
            },

            footerContent: createButtonSectionFactory(ResourceApi.Types.TextTriviaQuestion),

            fields: [
                {
                    type: 'radio',
                    name: 'type',
                    props: {
                        labelKey: 'editbreak.steps.text_trivia.question.type',
                        required: true,
                        options: [
                            {
                                value: "TEXT",
                                textKey: 'editbreak.steps.text_trivia.question.type.text'
                            },
                            {
                                value: "VIDEO",
                                textKey: 'editbreak.steps.text_trivia.question.type.video'
                            }
                        ]
                    }
                },
                {
                  type: 'contenteditable',
                  name: 'question',
                  required: true,
                  showField: (proxy) =>  (proxy.currentResource.type === 'TEXT'),
                  props: {
                    labelKey: 'editbreak.steps.text_trivia.question.question',
                    helpContent: <TextTriviaQuestionToolTip/>,
                    helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.question.question'}/></IntlProvider>
                  }
                },
                {
                  conditionalSection: true,
                  name: "useDefaultImage",
                  required: true,
                  props: {
                      labelKey: 'editbreak.steps.text_trivia.question.use_default_image'
                  },

                  fields: [
                    {
                      type: 'dropdown',
                      name: 'defaultImageId',
                      required: true,
                      props: {
                        labelKey: 'editbreak.steps.text_trivia.question.default_image',
                        options: [
                          {
                            text: '',
                            value: ''
                          },
                          {
                            text: 'Purple Rainbow Gradient',
                            value: '9812ff0a-22af-427d-9748-3fdf7913affc',
                            imageId: '9812ff0a-22af-427d-9748-3fdf7913affc'
                          },
                          {
                            text: 'Purple Shooting Star',
                            value: '304ad25b-6e1d-48d6-807f-17e695730bd2',
                            imageId: '304ad25b-6e1d-48d6-807f-17e695730bd2'
                          },
                          {
                            text: 'Purple Tie Dye',
                            value: 'ffc1637f-5073-4620-ab23-db03182712dd',
                            imageId: 'ffc1637f-5073-4620-ab23-db03182712dd'
                          },
                          {
                            text: 'Purple Sound Waves',
                            value: '79d4611a-d20f-4d5c-a798-f5df31ea2e7e',
                            imageId: '79d4611a-d20f-4d5c-a798-f5df31ea2e7e'
                          },
                          {
                            text: 'Purple Milky Way',
                            value: '21b1ff99-a40a-4a78-82aa-130d5d8f2b4b',
                            imageId: '21b1ff99-a40a-4a78-82aa-130d5d8f2b4b'
                          },
                          {
                            text: 'Black Gradient',
                            value: '9f35e19b-b5ee-4d3e-bdc2-c8ce269d886c',
                            imageId: '9f35e19b-b5ee-4d3e-bdc2-c8ce269d886c'
                          },
                          {
                            text: 'Plum Tapestry',
                            value: '425913d3-d34a-48d5-be99-294d250e6805',
                            imageId: '425913d3-d34a-48d5-be99-294d250e6805'
                          },
                          {
                            text: 'Purple Tapestry',
                            value: '85568cb5-94e0-485a-8e4b-c4b86d54bc88',
                            imageId: '85568cb5-94e0-485a-8e4b-c4b86d54bc88'
                          },
                          {
                            text: 'Purple Wood Panes',
                            value: '8afdf75d-c003-4f1b-a019-698422445412',
                            imageId: '8afdf75d-c003-4f1b-a019-698422445412'
                          }
                        ]
                      }
                    }
                  ]
                },
                {
                  type: 'image',
                  name: 'image',
                  required: false,
                  showField: (proxy) =>  (proxy.currentResource.type === 'TEXT' && !proxy.currentResource.useDefaultImage),
                  props: {
                    labelKey: 'editbreak.steps.text_trivia.question.image',
                    imageType: 'triviaQuestion',
                    helpContent: <TextTriviaQuestionImageToolTip/>,
                    helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.question.image'}/></IntlProvider>
                  }
                },
                {
                    type: 'textarea',
                    name: 'videoEmbed',
                    showField: (proxy) =>  (proxy.currentResource.type === 'VIDEO'),
                    required: true,
                    props: {
                        labelKey: 'editbreak.steps.text_trivia.question.video_embed'
                    }
                },
                {
                    name: 'answers',
                    subSection: true,
                    resource: true,
                    collection: true,
                    sectionWrapper: IndexedSubSectionWrapper,
                    deletable: true,
                    sortable: true,
                    sortableName: "answersOrder",
                    toggleSortLabelKey: 'editbreak.steps.text_trivia.answer.reorder_toggle',

                    resourceType: ResourceApi.Types.TextTriviaAnswer,
                    canDelete: (formProxy) => {
                      var parentQuestion = formProxy.parentCurrentResource;
                      return parentQuestion.answers && parentQuestion.answers.length > 2;
                    },

                    props: {
                        headerKey: 'editbreak.steps.text_trivia.answer.header',
                        getCustomSectionHeader: (answer, sectionIndex) => {
                          return  answer.type === 'TEXT'
                                  && answer.answer
                                  && answer.answer.trim() !== "" ? "Answer " + (sectionIndex+1) + ": " + answer.answer : null;
                        },
                        headerIcon: 'answer'
                    },

                    footerContent: createButtonSectionFactory(ResourceApi.Types.TextTriviaAnswer),

                    fields: [
                        // {
                        //     type: 'radio',
                        //     name: 'type',
                        //     required: true,
                        //     props: {
                        //         labelKey: 'editbreak.steps.text_trivia.answer.type',
                        //         options: [
                        //             {
                        //                 value: "TEXT",
                        //                 textKey: 'editbreak.steps.text_trivia.answer.type.text'
                        //             },
                        //             {
                        //                 value: "IMAGE",
                        //                 textKey: 'editbreak.steps.text_trivia.answer.type.image'
                        //             }
                        //         ]
                        //     }
                        // },
                        {
                          type: 'contenteditable',
                          name: 'answer',
                          
                          required: false,
                          placeholderKey: 'editbreak.steps.text_trivia.answer.answer.placeholder',
                          props: {
                            labelKey: 'editbreak.steps.text_trivia.answer.answer',
                            helpContent: <TextTriviaAnswerToolTip/>,
                          helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.answer.answer'}/></IntlProvider>
                          }
                        },
                        {
                          type: 'image',
                          name: 'image',
                          required: false,
                          
                          props: {
                            labelKey: 'editbreak.steps.text_trivia.answer.image',
                            imageType: 'triviaAnswer',
                            helpContent: <TextTriviaAnswerImageToolTip/>,
                            helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.answer.image'}/></IntlProvider>
                          }
                        },
                        {
                            type: 'yesnoradio',
                            name: 'correct',
                            required: true,
                            props: {
                                labelKey: 'editbreak.steps.text_trivia.answer.correct',
                                helpContent: <TextTriviaAnswerCorrectToolTip/>,
                                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.answer.correct'}/></IntlProvider>
                            }
                        },
                        {
                            conditionalSection: true,
                            name: "editButtonColors",
                            showField: (proxy) =>  (proxy.currentBaseResource.sponsoredType === 'sponsored'),
                            required: true,

                            props: {
                                labelKey: 'editbreak.steps.text_trivia.answer.edit_button_colors',
                                helpContent: <TextTriviaAnswerEditButtonColorsToolTip/>,
                                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.answer.edit_button_colors'}/></IntlProvider>
                            },

                            fields: [
                                {
                                    type: "color",
                                    name: "bgColor",
                                    props: {
                                        labelKey: "editbreak.steps.text_trivia.answer.bg_color",
                                        helpContent: <TextTriviaAnswerButtonBkgdColorToolTip/>,
                                        helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.answer.bg_color'}/></IntlProvider>
                                    }
                                },
                                {
                                    type: "color",
                                    name: "bgHoverColor",
                                    props: {
                                        labelKey: "editbreak.steps.text_trivia.answer.bg_hover_color"
                                    }
                                },
                                {
                                    type: "color",
                                    name: "textColor",
                                    props: {
                                        labelKey: "editbreak.steps.text_trivia.answer.text_color",
                                        helpContent: <TextTriviaAnswerButtonTextColorToolTip/>,
                                        helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.answer.text_color'}/></IntlProvider>
                                    }
                                },
                                {
                                    type: "color",
                                    name: "textHoverColor",
                                    props: {
                                        labelKey: "editbreak.steps.text_trivia.answer.text_hover_color"
                                    }
                                }
                            ]
                        },
                        
                    ]
                },
                {
                  type: 'contenteditable',
                  name: 'textAnswered',
                  props: {
                    required: true,
                    maxLength: 200,
                    helpContent: <TextTriviaQuestionResponseTextToolTip/>,
                    helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.question.response_text'}/></IntlProvider>,
                    labelKey: 'editbreak.steps.text_trivia.question.response_text',
                    validationRules: [
                        {
                            type: 'MarkupTextLimit',
                            params: {
                                limit: 200
                            }
                        }
                    ]
                  }
                },
                {
                  type: 'image',
                  name: 'textAnsweredImageId',
                  idFor: 'textAnsweredImage',
                  props: {
                    required: true,
                    labelKey: 'editbreak.steps.text_trivia.question.response_image',
                    imageType: 'triviaAnsweredImage',
                    helpContent: <TextTriviaQuestionResponseImageToolTip/>,
                  helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.question.response_image'}/></IntlProvider>
                  }
                },
                {
                    type: 'text',
                    name: 'ctaCopy',
                    
                    props: {
                      labelKey: 'CTA Copy',
                    }
                },
                {
                    type: 'text',
                    name: 'ctaUrl',
                    
                    props: {
                      labelKey: 'CTA Url',
                    }
                }



            ]
        }, // end questions
        {
            name: 'results',
            subSection: true,
            resource: true,
            collection: true,
            sectionWrapper: IndexedSubSectionWrapper,
            deletable: true,
            canDelete: (formProxy) => {
              var trivia = formProxy.parentCurrentResource;
              return trivia.results && trivia.results.length > 1;
            },
            sortable: true,
            sortableName: "textTriviaResultsOrder",
            toggleSortLabelKey: 'editbreak.steps.text_trivia.result.reorder_toggle',
            resourceType: ResourceApi.Types.TextTriviaResult,

            props: {
                headerKey: 'editbreak.steps.text_trivia.result.header',
                getCustomSectionHeader: (result, sectionIndex) => {
                  return  result.title
                          && result.title.trim() !== "" ? "Result " + (sectionIndex+1) + ': ' + result.title : null;
                }
            },

            footerContent: createButtonSectionFactory(ResourceApi.Types.TextTriviaResult),

            fields: [
                {
                    type: 'contenteditable',
                    name: 'title',
                    required: true,
                    props: {
                        labelKey: 'editbreak.steps.text_trivia.result.title',
                        helpContent: <TextTriviaResultTitleToolTip/>,
                        helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.result.title'}/></IntlProvider>
                    }
                },
                {
                  type: 'contenteditable',
                  name: 'desc',
                  required: false,
                  props: {
                    labelKey: 'editbreak.steps.text_trivia.result.description',
                    helpContent: <TextTriviaResultDescriptionToolTip/>,
                    helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.result.desription'}/></IntlProvider>
                  }
                },
                {
                  type: 'number',
                  name: 'minCorrect',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.text_trivia.result.min_correct',
                    helpContent: <TextTriviaResultMinCorrectToolTip/>,
                    helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.result.min_correct'}/></IntlProvider>
                  }
                },
                {
                  type: 'number',
                  name: 'maxCorrect',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.text_trivia.result.max_correct',
                    helpContent: <TextTriviaResultMaxCorrectToolTip/>,
                    helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.result.max_correct'}/></IntlProvider>
                  }
                },
                {
                  type: 'image',
                  name: 'imageId',
                  idFor: 'image',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.text_trivia.result.image',
                    imageType: 'triviaResult',
                    helpContent: <TextTriviaResultImageToolTip/>,
                    helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.result.image'}/></IntlProvider>
                  }
                },
                {
                  type: 'image',
                  name: 'shareImageId',
                  idFor: 'shareImage',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.text_trivia.result.share_image',
                    imageType: 'triviaResultShare',
                    helpContent: <TextTriviaResultSocialImageToolTip/>,
                    helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.text_trivia.result.share_image'}/></IntlProvider>
                  }
                },
                {
                    type: 'color',
                    name: 'backgroundColor',
                    // showField: (proxy) => {
                    //     console.log(proxy)
                    //     return (proxy.currentResource.sponsoredType === 'sponsored' && isNewLayout(proxy.currentResource))
                    // },
                    props: {
                        labelKey: 'editbreak.steps.text_trivia.result.backgroundColor',
                    }
                  },

                  {
                    type: 'image',
                    name: 'backgroundImageId',
                    idFor: 'backgroundImage',
                    // showField: (proxy) => (proxy.currentResource.sponsoredType === 'sponsored' && isNewLayout(proxy.currentResource)),
                    props: {
                        labelKey: 'editbreak.steps.text_trivia.result.backgroundColor',
                      imageType: 'stepBackgroundImage'
                    }
                  },

            ]
        } // end results
    ]
}



// WEBPACK FOOTER //
// ./public/components/EditContent/configs/breaks/steps/text-trivia.js